import React from "react";
import { formatChfOrDash } from "./unitDetailHelpers";
import { occupancyPercentText } from "../../../utils/occupancyPercent";

/**
 * UnitHeroBar — compact at-a-glance summary strip at the top of the Übersicht
 * tab. PURE PRESENTATIONAL: it only reads already-derived values off the props
 * (the Page's memos remain the single source of truth) and applies display-only
 * logic (formatting + the Gewinn sign→color decision). No data derivation,
 * no hooks, no fetches.
 *
 * Layout: a single grid, 2 columns on mobile → 4 columns on sm+, so the 8 cells
 * read as two rows on desktop — row 1 operations (Zimmer/Belegt/Reserviert/Frei),
 * row 2 finance (Potenzial/Umsatz/Gewinn/Auslastung).
 */
function HeroCell({ label, value, valueClassName }) {
  return (
    <div className="rounded-lg bg-slate-100 px-3 py-2 dark:bg-[#111520]">
      <p className="text-[9px] font-bold uppercase tracking-[0.8px] text-[#64748b] dark:text-[#6b7a9a]">
        {label}
      </p>
      <p
        className={`mt-1 text-lg font-bold tabular-nums ${
          valueClassName || "text-[#0f172a] dark:text-[#eef2ff]"
        }`}
      >
        {value}
      </p>
    </div>
  );
}

export default function UnitHeroBar({
  metrics,
  occupancyRate,
  unitKpiErr,
  unitKpiLoading,
}) {
  const profit = metrics?.currentProfit;
  const profitNum = Number(profit);
  const gewinnClass =
    profit == null || !Number.isFinite(profitNum)
      ? undefined
      : profitNum < 0
        ? "text-red-600 dark:text-red-400"
        : profitNum > 0
          ? "text-emerald-600 dark:text-emerald-400"
          : undefined;

  const count = (n) => (n == null ? "—" : n);

  return (
    <div
      data-testid="unit-hero-bar"
      className="rounded-[14px] border border-black/10 bg-white p-4 dark:border-white/[0.07] dark:bg-[#141824]"
    >
      {unitKpiErr ? (
        <p className="mb-3 text-sm text-[#f87171]">{unitKpiErr}</p>
      ) : null}
      {unitKpiLoading ? (
        <p className="mb-3 text-sm text-[#6b7a9a]">KPI werden geladen …</p>
      ) : null}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <HeroCell label="Zimmer" value={count(metrics?.totalRooms)} />
        <HeroCell label="Belegt" value={count(metrics?.occupiedCount)} />
        <HeroCell label="Reserviert" value={count(metrics?.reservedCount)} />
        <HeroCell label="Frei" value={count(metrics?.freeCount)} />
        <HeroCell label="Potenzial" value={formatChfOrDash(metrics?.fullRevenue)} />
        <HeroCell label="Umsatz" value={formatChfOrDash(metrics?.currentRevenue)} />
        <HeroCell
          label="Gewinn"
          value={formatChfOrDash(metrics?.currentProfit)}
          valueClassName={gewinnClass}
        />
        {/*
          AUSLASTUNG — the canonical rate, but only where there is a
          denominator (OC3). `formatPercent` turned a missing or zero-room
          figure into "0.0%", which read as "rooms exist and none are let" for
          a unit whose rooms are not recorded at all. `metrics.totalRooms` is
          the canonical total here -- the memo in `AdminUnitDetailPage`
          overrides it with `unitKpiOcc.total_rooms` when the KPI answered --
          so the dash decision uses the same number the "Zimmer" cell shows.
        */}
        <HeroCell
          label="Auslastung"
          value={occupancyPercentText({
            total: metrics?.totalRooms,
            rate: occupancyRate,
          })}
        />
      </div>
    </div>
  );
}
